import React from 'react';

export interface BaseInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange'> {
  label: string;
  error?: string;
  success?: boolean;
  loading?: boolean;
  variant?: 'default' | 'outlined' | 'filled';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  rightAddon?: React.ReactNode;
  helperText?: string;
  onChange?: (value: string) => void;
}

const inputStyles = {
  base: 'w-full px-4 py-3 rounded-md border border-gray-300 transition-all duration-200 ease-out focus:outline-none focus:ring-1 focus:ring-brand-electric-blue placeholder:text-gray-400',
  states: {
    error: 'border-red-500 text-red-900 placeholder:text-red-300 focus:border-red-500',
    success: 'border-green-500 focus:border-green-500',
    disabled: 'bg-brand-light-gray text-gray-500 cursor-not-allowed border-gray-200',
  },
  sizes: {
    sm: 'px-2 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-4 py-4 text-lg',
  },
};

const BaseInput = React.forwardRef<HTMLInputElement, BaseInputProps>(({
  label,
  error,
  success,
  loading,
  size = 'md',
  icon,
  rightAddon,
  helperText,
  onChange,
  className = '',
  ...props
}, ref) => {
  const stateClass = error
    ? inputStyles.states.error
    : success
    ? inputStyles.states.success
    : '';
  const disabledClass = props.disabled ? inputStyles.states.disabled : '';
  const sizeClass = inputStyles.sizes[size];

  return (
    <div className="space-y-1">
      {/* Visually hidden label for accessibility only */}
      {label && (
        <label className="sr-only" htmlFor={props.id || props.name}>
          {label}
        </label>
      )}
      <div className="relative flex items-center">
        {icon && <span className="absolute left-3">{icon}</span>}
        <input
          ref={ref}
          id={props.id || props.name}
          className={[
            inputStyles.base,
            sizeClass,
            stateClass,
            disabledClass,
            icon ? 'pl-10' : '',
            rightAddon ? 'pr-10' : '',
            className,
          ].join(' ')}
          aria-invalid={!!error}
          aria-describedby={error ? `${props.name}-error` : helperText ? `${props.name}-helper` : undefined}
          disabled={loading || props.disabled}
          onChange={e => onChange?.(e.target.value)}
          {...props}
        />
        {rightAddon && <span className="absolute right-3">{rightAddon}</span>}
      </div>
      {/* Error or Helper Text */}
      <div
        className={`overflow-hidden transition-all duration-200 ease-out ${error ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'}`}
        id={error ? `${props.name}-error` : undefined}
        aria-live="polite"
      >
        {error && (
          <div className="mt-2 text-sm text-red-600 text-left">{error}</div>
        )}
      </div>
      {helperText && !error && (
        <div className="mt-2 text-sm text-gray-500 text-left" id={`${props.name}-helper`}>
          {helperText}
        </div>
      )}
    </div>
  );
});

BaseInput.displayName = 'BaseInput';

export default BaseInput;